import { el, setChildren } from 'redom';

import internalEventCodes from 'event-system/codes/internal';

import application from 'application';
import serverInterface from 'server-interface';

import HelptapEditor from '../../../../helpers';

import FilterSelector from '../../data-collection/filter-selector';
import FieldsSelector from './field-selector';
import TextInput from 'generics/components/text-input';
import SingleSelectNative from 'generics/components/single-select/native';

import './index.scss';

class AdvancedDirectorySettings {

  constructor(configuration) {

    this.editor = configuration.editor;
    this.node = configuration.node;
    this.activeNodePath = configuration.activeNodePath;
    this.parentEntityId = configuration.parentEntityId;

    this.collection = null;
    this.view = null;

    this.fieldsSelector = null;
    this.filterSelector = null;
    this.pageSizeInput = null;
    this.conjunctionWordInput = null;

    this.filtersManager = application.dataManagers.get('DataCollectionFiltersManager');

    this.el = el('#advanced-data-collection-children-settings-main', [
      el('ht-spinner', {
        size: 'small'
      })
    ]);

  }

  renderInputs() {
    this.fieldsSelector = new FieldsSelector({
      editor: this.editor,
      node: this.node,
      activeNodePath: this.activeNodePath,
      collection: this.collection,
      view: this.view,
      onChange: this.handleFieldChange.bind(this)
    });

    this.filterSelector = new FilterSelector({
      application,
      pageId: this.parentEntityId,
      nodeId: this.node.properties.id,
      collectionId: this.node.properties.collectionId,
      filterId: this.view.filterId,
      onSelect: this.handleFilterSelection.bind(this)
    });

    this.pageSizeInput = new TextInput({
      type: 'number',
      value: this.view.pageSize || 0,
      placeholder: 'All',
      onChange: this.handlePageSizeChange.bind(this)
    });

    const conjunctionWords = [
      { value: 'and', label: 'and' },
      { value: 'or', label: 'or' },
      { value: 'with', label: 'with' },
      { value: 'of', label: 'of' },
      { value: 'for', label: 'for' },
      { value: 'in', label: 'in' },
      { value: 'from', label: 'from' },
      { value: 'to', label: 'to' },
      { value: 'by', label: 'by' },
      { value: 'at', label: 'at' }
    ];

    const selectedConjunctionIndex = conjunctionWords.findIndex(function findIndex(option) {
      return option.value === this.view.conjunctionWord;
    }.bind(this));

    this.conjunctionWordInput = new SingleSelectNative({
      onChange: this.handleConjunctionWordChange.bind(this),
      placeholder: 'Select conjunction word',
      labelFieldName: 'label',
      valueFieldName: 'value',
      options: conjunctionWords,
      selectedOptionIndices: selectedConjunctionIndex >= 0 ? [selectedConjunctionIndex] : []
    });

    setChildren(this.el, [
      this.fieldsSelector,
      this.filterSelector,
      el('.form-input-row', [
        el('label.side-value-control-label.field-label', 'Items per page'),
        this.pageSizeInput
      ]),
      el('.form-input-row', [
        el('label.side-value-control-label.field-label', 'Query pages modifier'),
        this.conjunctionWordInput
      ])
    ]);

  }

  async initialize() {

    const [collection, result] = await Promise.all([
      serverInterface.startup.dataCollections.getPopulatedCollectionById(this.node.properties.collectionId),
      serverInterface.startup.dataCollectionViews.getDataCollectionViewForEditor(this.node.properties.collectionViewId),
      this.filtersManager.loadAll(this.node.properties.collectionId)
    ]);

    this.collection = collection;
    this.view = result.view;

    this.renderInputs();

  }

  onmount() {

    this.initialize();

  }

  async handleFilterSelection(filterId) {

    HelptapEditor.setAttributes(this.editor, { properties: { ...this.node.properties, updatedAt: Date.now() } }, this.activeNodePath);

    serverInterface.startup.dataCollectionViews.updateDataCollectionViewFilterById(this.node.properties.collectionViewId, { filterId });

  }

  handleFieldChange() {

    HelptapEditor.setAttributes(this.editor, { properties: { ...this.node.properties, updatedAt: Date.now() } }, this.activeNodePath);

  }

  async handlePageSizeChange(event, value) {

    let pageSize = parseInt(value, 10);

    if (isNaN(pageSize)) {
      pageSize = 0;
    }

    if (pageSize < 0) {
      pageSize = 0;
    }

    if (pageSize > 100) {
      pageSize = 100;
    }

    this.pageSizeInput.setValue(pageSize);

    await serverInterface.startup.dataCollectionViews.updateDataCollectionViewPageSizeById(this.node.properties.collectionViewId, { pageSize });

    HelptapEditor.setAttributes(this.editor, { properties: { ...this.node.properties, updatedAt: Date.now() } }, this.activeNodePath);

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
      message: 'By default we only show 10 entries in the editor. To see the page size entered, please check published page',
      seconds: 5,
      type: 'info'
    });

  }

  async handleConjunctionWordChange({ values }) {

    const conjunctionWord = values[0] || '';

    await serverInterface.startup.dataCollectionViews.updateDataCollectionViewConjunctionWordById(this.node.properties.collectionViewId, { conjunctionWord });

    HelptapEditor.setAttributes(this.editor, { properties: { ...this.node.properties, updatedAt: Date.now() } }, this.activeNodePath);

  }

  update(configuration) {

    this.editor = configuration.editor;
    this.node = configuration.node;
    this.activeNodePath = configuration.activeNodePath;
    this.parentEntityId = configuration.parentEntityId;

    this.fieldsSelector?.update(configuration);
    this.filterSelector?.update({
      application,
      pageId: this.parentEntityId,
      nodeId: this.node.properties.id,
      collectionId: this.node.properties.collectionId,
      filterId: this.node.properties.filterId,
      onSelect: this.handleFilterSelection.bind(this)
    });

  }

}

export default AdvancedDirectorySettings;